/**
 * notifications.ts — notifikasjonsstore for sanntidsvarsler (singleton)
 *
 * Samler SSE-events fra user-stream i minnet og eksponerer dem for
 * UiShell sitt notifikasjonspanel. Ingen React-avhengighet — store er
 * en ren modul, og React-komponenter abonnerer via subscribe().
 *
 * Dataflyt (se plan 2026-09-23):
 *   Backend SSE → user-stream.ts → UserStreamListener
 *     → mapEventToNotification() → addNotification()
 *     → playNotificationSound() (Notification.mp3, én gang per event)
 *     → UiShell leser via useNotifications()
 *
 * Designvalg:
 * 1. Minne-only — varsler forsvinner ved refresh. Historikk krever
 *    backend-endepunkt som ikke finnes ennå.
 * 2. Kap på MAX_NOTIFICATIONS — eldste kastes først (ringbuffer-atferd).
 * 3. Egne events (brukerens egen chatmelding ekkoes tilbake) gir INGEN
 *    notifikasjon og INGEN lyd — filtreres i mapEventToNotification.
 * 4. Lyd: Notification.mp3 spilles ved MOTTAKELSE (addNotification),
 *    ikke ved visning. Én avspilling per event — store er singleton.
 */

import type { ReactNode } from 'react';

/** Notifikasjons-kind — samme konvensjon som Toast (fargekoding i panel). */
export type NotificationKind = 'info' | 'success' | 'warning' | 'error';

/** Én notifikasjon i panelet. */
export interface AppNotification {
  /** Unik id — `${eventType}:${message_id ?? timestamp}` */
  id: string;
  /** SSE event-type den kom fra (f.eks. 'chat_message') */
  eventType: string;
  kind: NotificationKind;
  title: string;
  subtitle?: string;
  /** ISO-tidsstempel for mottakelse */
  createdAt: string;
  /** Ulest-flagg — panelet nullstiller alle ved åpning */
  read: boolean;
}

/** Rå SSE-payload for chat_message (snake_case fra backend). */
interface ChatMessagePayload {
  message_id: number;
  chat_id: number;
  user_id: string;
  message_content: string;
  [key: string]: unknown;
}

/** Maks antall varsler i minnet — eldste kastes først. */
const MAX_NOTIFICATIONS = 50;

/** Innlogget bruker-ID — settes av UserStreamListener ved oppstart.
 *  Brukes til å filtrere bort brukerens EGEN events (ingen lyd/varsling
 *  på det man selv sendte). */
let currentUserId: string | null = null;

/** Sett innlogget bruker-ID (kalles av UserStreamListener ved mount/login). */
export function setNotificationUserId(userId: string | null): void {
  currentUserId = userId;
}

// === Store-tilstand (modul-nivå, singleton) ===
let notifications: AppNotification[] = [];
let unreadCount = 0;
const subscribers = new Set<() => void>();

/** Abonner på endringer — returnerer unsubscribe-funksjon. */
export function subscribeNotifications(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/** Snapshot for useSyncExternalStore — må være stabil referanse ved ingen endring. */
export function getNotificationsSnapshot(): AppNotification[] {
  return notifications;
}

/** Uleste-teller for useSyncExternalStore (primitiv — billig sammenligning). */
export function getUnreadCountSnapshot(): number {
  return unreadCount;
}

function emit(): void {
  for (const listener of subscribers) {
    try {
      listener();
    } catch {
      // Én feilende lytter stopper de andre
    }
  }
}

/**
 * playNotificationSound — spiller Notification.mp3 fra /public/Sound.
 *
 * Samme mønster som Toast.tsx: ny Audio-instans per avspilling (gjentatte
 * varsler kolliderer ikke), autoplay-policy feiler stille (nettleseren kan
 * blokkere lyd uten forutgående brukerinteraksjon).
 */
function playNotificationSound(): void {
  if (typeof window === 'undefined') return;
  try {
    const audio = new Audio('/Sound/Notification.mp3');
    void audio.play().catch(() => {
      // Autoplay-policy — feiler stille (samme valg som Toast.tsx)
    });
  } catch {
    // Audio-konstruksjon kan feile i ikke-nettleser-miljøer — stille.
  }
}

/**
 * mapEventToNotification — leser SSE event-TYPE og mapper til notifikasjon.
 *
 * Kontrakt (brukerbeslutning 2026-09-23): typen avgjør notifikasjonstypen.
 * - 'chat_message' → info, "Ny chatmelding" (kun hvis IKKE brukerens egen)
 * - ukjente typer → generisk varsel med eventType som tittel (fremtidssikret:
 *   nye backend-events vises automatisk uten kodeendring her)
 *
 * @returns AppNotification, eller null hvis eventet skal ignoreres
 *          (brukerens egen melding)
 */
export function mapEventToNotification(
  eventType: string,
  data: unknown
): AppNotification | null {
  const now = new Date().toISOString();

  if (eventType === 'chat_message') {
    const payload = data as ChatMessagePayload;
    // Egne meldinger skal ikke varsles (brukeren sendte dem selv)
    if (currentUserId !== null && payload.user_id === currentUserId) {
      return null;
    }
    return {
      id: `chat_message:${payload.message_id ?? now}`,
      eventType,
      kind: 'info',
      title: 'Ny chatmelding',
      subtitle: `Chat #${payload.chat_id ?? '?'}`,
      createdAt: now,
      read: false,
    };
  }

  // Ukjent event-type — generisk varsel (fremtidssikret)
  return {
    id: `${eventType}:${now}`,
    eventType,
    kind: 'info',
    title: `Ny hendelse: ${eventType}`,
    createdAt: now,
    read: false,
  };
}

/**
 * addNotification — legger inn et varsel, spiller lyd, varsler lyttere.
 *
 * @returns true hvis varselet ble lagt inn (og lyd spilt), false hvis
 *          det ble filtrert bort (null-input eller duplikat-id)
 */
export function addNotification(
  notification: AppNotification | null
): boolean {
  if (!notification) return false;

  // Dedup: samme id (f.eks. samme melding via SSE-reconnect) ignoreres
  if (notifications.some((n) => n.id === notification.id)) return false;

  notifications = [notification, ...notifications].slice(0, MAX_NOTIFICATIONS);
  unreadCount += 1;

  // Lyd ved MOTTAKELSE — én gang per event (store er singleton)
  playNotificationSound();

  emit();
  return true;
}

/** Marker alle varsler som lest (kalles når panelet åpnes). */
export function markAllNotificationsRead(): void {
  if (unreadCount === 0) return;
  notifications = notifications.map((n) => ({ ...n, read: true }));
  unreadCount = 0;
  emit();
}

/** Tøm alle varsler (f.eks. ved logout). */
export function clearNotifications(): void {
  notifications = [];
  unreadCount = 0;
  emit();
}
